/**
 * Default threshold definitions for driver scorecard fields.
 *
 * Shape per field:
 *   direction: 'higher_better' | 'lower_better'
 *   good / warning: the numeric boundaries (exact semantics depend on direction)
 *   For compiled_score: elite / solid / coaching boundaries (4-tier)
 *   For customer_requests: only a 'good' boundary (>= good → success, else warning)
 *
 * Rendering classes produced by getFieldClass():
 *   Standard 3-tier (higher_better): value >= good → text-success, >= warning → text-warning, else text-danger
 *   Standard 3-tier (lower_better):  value <  good → text-success, <= warning → text-warning, else text-danger
 *   customer_requests (2-tier):      value >= good → text-success, else text-warning
 *   compiled_score (4-tier, main):   value >= elite → compiled-score-elite, >= solid → compiled-score-solid,
 *                                    >= coaching → compiled-score-coaching, else compiled-score-highrisk
 *   compiled_score (comparison):     same numeric bands but Bootstrap text classes
 */

export const DEFAULT_SCORECARD_THRESHOLDS = {
  risk_score: {
    label: 'Risk Score',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
  risk_rank: {
    label: 'Risk Rank (Percentile)',
    direction: 'lower_better',
    good: 25,
    warning: 75,
  },
  idling_percentage: {
    label: 'Idling %',
    direction: 'lower_better',
    good: 21.8,
    warning: 30,
  },
  miles_per_gallon: {
    label: 'MPG',
    direction: 'higher_better',
    good: 7.1,
    warning: 6.6,
  },
  dvir_completion_rate: {
    label: 'DVIR Completion %',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
  customer_requests: {
    label: 'Customer Requests',
    direction: 'higher_better',
    good: 1,
  },
  customer_requests_per_10k_miles: {
    label: 'Customer Requests / 10k Miles',
    direction: 'higher_better',
    good: 1,
  },
  compiled_score: {
    label: 'Compiled Score',
    direction: 'higher_better',
    elite: 85,
    solid: 65,
    coaching: 40,
  },
}

/**
 * Optional scorecard fields — not colored by default.
 * A field only becomes active (colored) when the tenant explicitly configures it
 * and saves it into scorecard_thresholds, at which point it appears in mergeThresholds().
 */
export const OPTIONAL_SCORECARD_DEFAULTS = {
  speeding_percentage: {
    label: 'Speeding %',
    direction: 'lower_better',
    good: 5,
    warning: 15,
  },
  distracted_driving_percentage: {
    label: 'Distracted Driving / 10k',
    direction: 'lower_better',
    good: 5,
    warning: 15,
  },
  following_distance_per_10k_miles: {
    label: 'Following Distance / 10k',
    direction: 'lower_better',
    good: 5,
    warning: 15,
  },
  weekend_availability_percentage: {
    label: 'Weekend Availability %',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
  idle_hours: {
    label: 'Idle Hours',
    direction: 'lower_better',
    good: 10,
    warning: 25,
  },
  fuel_consumed_gallons: {
    label: 'Fuel Consumed (gal)',
    direction: 'lower_better',
    good: 200,
    warning: 350,
  },
  bus_days: {
    label: 'Bus Days',
    direction: 'higher_better',
    good: 18,
    warning: 10,
  },
}

/**
 * UI configuration for every configurable scorecard field.
 * Used by the threshold modal to drive sliders generically.
 *
 *   tier  '3' = dual-handle (good + warning)
 *         '2' = single-handle (good cutoff only)
 *         '4' = triple-handle (elite / solid / coaching — compiled score only)
 *   scale  slider max value
 *   step   slider / number input increment
 *   minVal minimum value for the slider (defaults to 0 when absent)
 */
export const THRESHOLD_FIELD_CONFIG = {
  risk_score:                       { label: 'Risk Score',                  direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  risk_rank:                        { label: 'Risk Rank (Percentile)',      direction: 'lower_better',  tier: '3', scale: 100, step: 1   },
  idling_percentage:                { label: 'Idling %',                    direction: 'lower_better',  tier: '3', scale: 100, step: 0.5 },
  miles_per_gallon:                 { label: 'MPG',                         direction: 'higher_better', tier: '3', scale: 15,  step: 0.1 },
  dvir_completion_rate:             { label: 'DVIR Completion %',           direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  customer_requests:                { label: 'Customer Requests',           direction: 'higher_better', tier: '2', scale: 20,  step: 1,   minVal: 1 },
  customer_requests_per_10k_miles:  { label: 'Cust. Requests / 10k Mi',    direction: 'higher_better', tier: '2', scale: 10,  step: 0.1 },
  compiled_score:                   { label: 'Compiled Score',              direction: 'higher_better', tier: '4', scale: 100, step: 1   },
  speeding_percentage:              { label: 'Speeding %',                  direction: 'lower_better',  tier: '3', scale: 100, step: 0.5 },
  distracted_driving_percentage:    { label: 'Distracted Driving / 10k',   direction: 'lower_better',  tier: '3', scale: 50,  step: 0.5 },
  following_distance_per_10k_miles: { label: 'Following Distance / 10k',   direction: 'lower_better',  tier: '3', scale: 50,  step: 0.5 },
  weekend_availability_percentage:  { label: 'Weekend Availability %',     direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  idle_hours:                       { label: 'Idle Hours',                  direction: 'lower_better',  tier: '3', scale: 100, step: 0.5 },
  fuel_consumed_gallons:            { label: 'Fuel Consumed (gal)',         direction: 'lower_better',  tier: '3', scale: 1000, step: 5  },
  bus_days:                         { label: 'Bus Days',                    direction: 'higher_better', tier: '3', scale: 25,  step: 1   },
}

/** Keys that are always shown in the threshold modal (colored by default). */
export const DEFAULT_THRESHOLD_KEYS = Object.keys(DEFAULT_SCORECARD_THRESHOLDS)

/**
 * Merge tenant threshold overrides onto the defaults.
 * When tenantThresholds is null/undefined (never saved), falls back to DEFAULT_SCORECARD_THRESHOLDS.
 * When tenantThresholds is an object (even {}), only the fields present in it are colored —
 * a tenant can remove any field including defaults by saving without it.
 * Optional fields are only included when the tenant has explicitly added them.
 *
 * @param {Object|null} tenantThresholds  - parsed scorecard_thresholds from the API response
 * @returns {Object} merged thresholds keyed by field name
 */
export function mergeThresholds(tenantThresholds) {
  // null/undefined means "never configured" — use hardcoded defaults
  // An empty object {} means the tenant explicitly cleared all fields — return empty (no coloring)
  if (tenantThresholds === null || tenantThresholds === undefined) {
    return { ...DEFAULT_SCORECARD_THRESHOLDS }
  }
  if (typeof tenantThresholds !== 'object') {
    return { ...DEFAULT_SCORECARD_THRESHOLDS }
  }
  const merged = {}

  // Include only fields the tenant has explicitly configured
  for (const key of Object.keys(DEFAULT_SCORECARD_THRESHOLDS)) {
    const def = DEFAULT_SCORECARD_THRESHOLDS[key]
    const override = tenantThresholds[key]
    if (override && typeof override === 'object') {
      const mf = { ...def }
      for (const prop of ['good', 'warning', 'elite', 'solid', 'coaching']) {
        if (override[prop] !== undefined && override[prop] !== null && !isNaN(Number(override[prop]))) {
          mf[prop] = Number(override[prop])
        }
      }
      merged[key] = mf
    }
    // no else — if the tenant removed this field, don't color it
  }

  // Optional fields — only included when the tenant has explicitly configured them
  for (const key of Object.keys(OPTIONAL_SCORECARD_DEFAULTS)) {
    if (tenantThresholds[key] && typeof tenantThresholds[key] === 'object') {
      const def = OPTIONAL_SCORECARD_DEFAULTS[key]
      const override = tenantThresholds[key]
      const mf = { ...def }
      for (const prop of ['good', 'warning']) {
        if (override[prop] !== undefined && override[prop] !== null && !isNaN(Number(override[prop]))) {
          mf[prop] = Number(override[prop])
        }
      }
      merged[key] = mf
    }
  }

  return merged
}

/**
 * Return the Bootstrap/CSS class for a standard 3-tier scorecard field.
 *
 * @param {string} fieldName  - key in thresholds (e.g. 'risk_score')
 * @param {number|null} value - the raw metric value
 * @param {Object} thresholds - merged thresholds from mergeThresholds()
 * @param {Object} [context]  - extra data needed for some fields, e.g. { total_drivers }
 * @returns {string} CSS class
 */
export function getFieldClass(fieldName, value, thresholds, context = {}) {
  if (value === null || value === undefined) return ''

  const cfg = thresholds[fieldName]
  if (!cfg) return ''

  const v = Number(value)
  if (!isFinite(v)) return ''

  if (fieldName === 'compiled_score') {
    return getCompiledScoreClass(v, cfg)
  }

  if (fieldName === 'compiled_score_comparison') {
    return getCompiledScoreComparisonClass(v, cfg)
  }

  if (fieldName === 'customer_requests' || fieldName === 'customer_requests_per_10k_miles') {
    const good = cfg.good !== undefined ? Number(cfg.good) : 1
    return v >= good ? 'text-success' : 'text-warning'
  }

  if (fieldName === 'risk_rank') {
    if (!context.total_drivers || context.total_drivers === 0) return ''
    const percentile = (v / context.total_drivers) * 100
    return _bandClass(percentile, cfg, 'lower_better')
  }

  return _bandClass(v, cfg, cfg.direction)
}

function _bandClass(v, cfg, direction) {
  if (direction === 'higher_better') {
    // Boundaries: v >= good → green, v >= warning → yellow, else red (strict lower bound)
    if (v >= cfg.good) return 'text-success'
    if (v >= cfg.warning) return 'text-warning'
    return 'text-danger'
  } else {
    // lower_better
    // Boundaries: v < good → green (exclusive), v <= warning → yellow (inclusive), else red.
    // The asymmetry (< vs <=) keeps the good/warning boundary in the yellow band for lower_better fields.
    if (v < cfg.good) return 'text-success'
    if (v <= cfg.warning) return 'text-warning'
    return 'text-danger'
  }
}

/**
 * 4-tier class for the main scorecard badge (uses scoped CSS classes).
 */
export function getCompiledScoreClass(score, cfg) {
  if (!cfg) return ''
  if (score === null || score === undefined) return ''
  const s = Number(score)
  if (!isFinite(s)) return ''
  const elite    = cfg.elite    !== undefined ? cfg.elite    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.elite
  const solid    = cfg.solid    !== undefined ? cfg.solid    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.solid
  const coaching = cfg.coaching !== undefined ? cfg.coaching : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.coaching
  if (s >= elite)    return 'compiled-score-elite'
  if (s >= solid)    return 'compiled-score-solid'
  if (s >= coaching) return 'compiled-score-coaching'
  return 'compiled-score-highrisk'
}

/**
 * 4-tier class for the comparison table (uses Bootstrap text classes).
 */
export function getCompiledScoreComparisonClass(score, cfg) {
  if (!cfg) return ''
  if (score === null || score === undefined) return ''
  const s = Number(score)
  if (!isFinite(s)) return ''
  const elite    = cfg.elite    !== undefined ? cfg.elite    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.elite
  const solid    = cfg.solid    !== undefined ? cfg.solid    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.solid
  const coaching = cfg.coaching !== undefined ? cfg.coaching : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.coaching
  if (s >= elite)    return 'text-success'
  if (s >= solid)    return 'text-primary'
  if (s >= coaching) return 'text-warning'
  return 'text-danger'
}
